import { rocket } from 'datastar'

const pendingLoads = new Map<string, Promise<unknown>>()
const loadedURLs = new Set<string>()
let pendingLoadCount = 0

const selfTag = 'stellar-wc-loader'

const parseWCPrefixes = (value: unknown) => {
  const raw = Array.isArray(value)
    ? value
    : (() => {
        const trimmed = `${value ?? ''}`.trim()
        if (!trimmed) return []
        if (trimmed.startsWith('[')) {
          try {
            const parsed = JSON.parse(trimmed)
            if (Array.isArray(parsed)) return parsed
            if (typeof parsed === 'string') return [parsed]
          } catch {}
        }
        return trimmed.split(',')
      })()
  return [
    ...new Set(
      raw.map((prefix) => `${prefix}`.trim().replace(/-+$/, '')).filter(Boolean),
    ),
  ].sort((a, b) => b.length - a.length)
}

rocket(selfTag, {
  mode: 'light', // Non-visual document loader; it needs to observe and affect the real page DOM.
  props: ({ bool, string }) => ({
    urlPrefix: string,
    wcPrefix: string,
    uncloak: bool,
  }),
  setup({ cleanup, host, observeProps, overrideProp }) {
    host.hidden = true
    let settleToken = 0
    const settle = () => {
      const token = ++settleToken
      queueMicrotask(() => {
        if (token !== settleToken || pendingLoadCount !== 0) return
        requestAnimationFrame(() => {
          if (token !== settleToken || pendingLoadCount !== 0) return
          if (host.uncloak) document.body.classList.remove('cloak')
        })
      })
    }
    const load = async (root: Node) => {
      const wcPrefixes = parseWCPrefixes(host.wcPrefix)
      if (!host.urlPrefix || wcPrefixes.length === 0) {
        settle()
        return
      }
      const requests: Promise<unknown>[] = []
      for (const element of (() => {
        if (root instanceof Element)
          return [root, ...root.querySelectorAll('*')]
        if (root instanceof Document || root instanceof DocumentFragment) {
          return [...root.querySelectorAll('*')]
        }
        return []
      })()) {
        const tag = element.localName
        if (tag === selfTag || customElements.get(tag)) continue
        const prefix = wcPrefixes.find((candidate) =>
          tag.startsWith(`${candidate}-`),
        )
        if (!prefix) continue
        const url = `${host.urlPrefix.endsWith('/') ? host.urlPrefix : `${host.urlPrefix}/`}${tag.slice(prefix.length + 1)}.js`
        if (loadedURLs.has(url)) continue
        const current = pendingLoads.get(url)
        if (current) {
          requests.push(current)
          continue
        }
        pendingLoadCount += 1
        const request = (async () => {
          try {
            const module = await import(url)
            loadedURLs.add(url)
            return module
          } finally {
            pendingLoadCount -= 1
            settle()
          }
        })()
        const trackedRequest = (async () => {
          try {
            return await request
          } catch (error) {
            pendingLoads.delete(url)
            throw error
          }
        })()
        pendingLoads.set(url, trackedRequest)
        requests.push(trackedRequest)
      }
      if (requests.length === 0) {
        settle()
        return
      }
      await Promise.all(requests)
      settle()
    }
    const startLoad = (root: Node) => {
      void load(root).catch((error) => {
        if (typeof reportError === 'function') {
          reportError(error)
          return
        }
        setTimeout(() => {
          throw error
        })
      })
    }
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) startLoad(node)
      }
    })
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    })
    overrideProp(
      'wcPrefix',
      (getDefault) => {
        const raw = getDefault()
        if (typeof raw !== 'string') return raw
        const parsed = parseWCPrefixes(raw)
        return raw.trim().startsWith('[') ? parsed : (parsed[0] ?? '')
      },
      (nextValue, setDefault) =>
        setDefault(Array.isArray(nextValue) ? JSON.stringify(nextValue) : `${nextValue ?? ''}`),
    )
    observeProps(() => startLoad(document), 'urlPrefix', 'wcPrefix', 'uncloak')
    queueMicrotask(() => startLoad(document))
    cleanup(() => observer.disconnect())
  },
  renderOnPropChange: false,
  render: () => '',
})
